// ✅ src/components/ViewUser.js
import React, { useEffect, useMemo, useState } from "react";
import "../styles/UserForm.css";
import { getTenants } from "../services/tenantService";
import { getLocationsByTenant } from "../services/locationService";
import { getDepartmentsByLocation } from "../services/departmentService";

function ViewUser({ userData, onClose, onEdit }) {
  const [tenantName, setTenantName] = useState("");
  const [locationName, setLocationName] = useState("");
  const [departmentName, setDepartmentName] = useState("");

  const tenantId = useMemo(() => userData?.tenant_id ?? "", [userData]);
  const locationId = useMemo(() => userData?.location_id ?? "", [userData]);
  const departmentId = useMemo(() => userData?.department_id ?? "", [userData]);

  // 1) Resolve Tenant name
  useEffect(() => {
    let cancelled = false;
    const existing = (userData?.tenant_name || "").trim();
    if (existing) {
      setTenantName(existing);
      return;
    }
    if (!tenantId) {
      setTenantName("");
      return;
    }
    (async () => {
      try {
        const res = await getTenants();
        const list = res?.data || [];
        const found = list.find((t) => String(t.id) === String(tenantId));
        if (!cancelled) setTenantName(found?.name || `Tenant #${tenantId}`);
      } catch {
        if (!cancelled) setTenantName(`Tenant #${tenantId}`);
      }
    })();
    return () => (cancelled = true);
  }, [tenantId, userData?.tenant_name]);

  // 2) Resolve Location name
  useEffect(() => {
    let cancelled = false;
    const existing = (userData?.location_name || "").trim();
    if (existing) {
      setLocationName(existing);
      return;
    }
    if (!tenantId || !locationId) {
      setLocationName("");
      return;
    }
    (async () => {
      try {
        const res = await getLocationsByTenant(tenantId);
        const list = res?.data || [];
        const found = list.find((l) => String(l.id) === String(locationId));
        if (!cancelled) setLocationName(found?.name || "");
      } catch {
        if (!cancelled) setLocationName("");
      }
    })();
    return () => (cancelled = true);
  }, [tenantId, locationId, userData?.location_name]);

  // 3) Resolve Department name
  useEffect(() => {
    let cancelled = false;
    const existing = (userData?.department_name || "").trim();
    if (existing) {
      setDepartmentName(existing);
      return;
    }
    if (!tenantId || !locationId || !departmentId) {
      setDepartmentName("");
      return;
    }
    (async () => {
      try {
        const res = await getDepartmentsByLocation(tenantId, locationId);
        const list = res?.data || [];
        const found = list.find((d) => String(d.id) === String(departmentId));
        if (!cancelled) setDepartmentName(found?.name || "");
      } catch {
        if (!cancelled) setDepartmentName("");
      }
    })();
    return () => (cancelled = true);
  }, [tenantId, locationId, departmentId, userData?.department_name]);

  if (!userData) return null;

  return (
    <div className="uf-modal" onClick={onClose}>
      <div className="uf-card" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="uf-header">
          <h5>User Profile</h5>
          <button onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div className="uf-body">
          <div className="uf-grid">
            {/* Username */}
            <div>
              <label className="form-label">Username</label>
              <input className="form-control" readOnly value={userData.username || ""} />
            </div>

            {/* Email */}
            <div>
              <label className="form-label">Email</label>
              <input className="form-control" readOnly value={userData.email || ""} />
            </div>

            {/* Tenant */}
            <div>
              <label className="form-label">Tenant</label>
              <input
                className="form-control"
                readOnly
                value={tenantName || (tenantId ? `Tenant #${tenantId}` : "")}
              />
            </div>

            {/* Role */}
            <div>
              <label className="form-label">Role</label>
              <input
                className="form-control"
                readOnly
                value={userData.role_name || (userData.role_id ? `Role #${userData.role_id}` : "")}
              />
            </div>

            {/* Locations (read-only multi) */}
            <div>
              <label className="form-label">Locations</label>
              <select className="form-select uf-select" multiple disabled>
                {locationName
                  ? <option>{locationName}</option>
                  : (locationId ? <option>Location #{locationId}</option> : null)}
              </select>
            </div>

            {/* Departments (read-only multi) */}
            <div>
              <label className="form-label">Departments</label>
              <select className="form-select uf-select" multiple disabled>
                {departmentName
                  ? <option>{departmentName}</option>
                  : (departmentId ? <option>Department #{departmentId}</option> : null)}
              </select>
            </div>

            {/* Is Employee */}
            <div className="form-check form-switch mt-4">
              <input type="checkbox" className="form-check-input" disabled checked={!!userData.is_employee} />
              <label className="form-check-label">Also an Employee?</label>
            </div>

            {/* Status */}
            <div className="mt-3">
              <label className="form-label d-block">Status</label>
              <span className={`badge ${userData.is_active ? "bg-success" : "bg-secondary"}`}>
                {userData.is_active ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="uf-actions">
          <button type="button" className="btn btn-cancel" onClick={onClose}>
            Close
          </button>
          <button className="btn btn-primary" onClick={() => onEdit?.(userData)}>
            Edit User
          </button>
        </div>
      </div>
    </div>
  );
}

export default ViewUser;
